import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Collapse,
  Box,
  Typography,
  IconButton,
  Button,
  Stack,
  FormControl,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Common reusable dialog to display error details for bulk uploads or similar tables.
 *
 * Props:
 * - open: boolean -> controls dialog visibility
 * - onClose: function -> callback when dialog closed
 * - title: string -> dialog title
 * - tableColumns: array[string] -> column headers
 * - uploadColumns: array[string] -> keys used in each row of data
 * - dataList: array[object] -> list of rows (must include row.ErrorText for errors)
 */
const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiDialog-paper": {
    width: "800px",
    maxWidth: "90%",
  },
}));

function BootstrapDialogTitle(props) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

const ErrorTableDialog = ({
  open,
  onClose,
  title = "Error Details",
  tableColumns = [],
  uploadColumns = [],
  dataList = [],
}) => {
  const [openRows, setOpenRows] = useState({});

  const toggleRow = (index) => {
    setOpenRows((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  if (!dataList || dataList.length === 0) return null;

  return (
    <BootstrapDialog
      onClose={onClose}
      open={open}
      aria-labelledby="customized-dialog-title"
    >
      <BootstrapDialogTitle onClose={onClose} id="customized-dialog-title">
        <Typography
          level="h3"
          component="h3"
          fontWeight="600"
          fontSize="2rem"
          className="h3"
        >
          {title}
        </Typography>
      </BootstrapDialogTitle>

      <DialogContent
        className="modal-dialog-content font-size-14"
        dividers
        sx={{ overflowX: "auto" }}
      >
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 400,
            mt: 2,
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow
              sx={{
                background: "#000",
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
              >
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    background: "transparent",
                    color: "white",
                    textAlign: "center",
                    width: 60,
                  }}
                >
                  Info
                </TableCell>
                {tableColumns.map((col) => (
                  <TableCell
                    key={col}
                    sx={{
                      fontWeight: "bold",
                      background: "transparent",
                      color: "white",
                    }}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {dataList.map((row, i) => {
                const hasError = !!row.ErrorText;
                const isOpen = !!openRows[i];

                return (
                  <React.Fragment key={i}>
                    <TableRow
                      sx={{
                        backgroundColor: hasError ? "#fff8f8" : "inherit",
                      }}
                    >
                      <TableCell align="center">
                        {hasError && (
                          <IconButton
                            size="small"
                            sx={{ color: "#f44336" }}
                            onClick={() => toggleRow(i)}
                          >
                            {isOpen ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <InfoOutlinedIcon />
                            )}
                          </IconButton>
                        )}
                      </TableCell>

                      {uploadColumns.map((col, j) => (
                        <TableCell key={j}>
                          {col === "rowIndex" ? row[col] || i + 1 : row[col]}
                        </TableCell>
                      ))}
                    </TableRow>

                    {hasError && (
                      <TableRow>
                        <TableCell
                          colSpan={uploadColumns.length + 1}
                          sx={{ p: 0 }}
                        >
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                p: 2,
                                backgroundColor: "#fff1f0",
                                borderTop: "1px solid #f44336",
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="error"
                                sx={{ whiteSpace: "pre-line" }}
                              >
                                {row.ErrorText}
                              </Typography>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions style={{ padding: "15px" }}>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <FormControl>
            <Button
              type="submit"
              onClick={onClose}
              variant="contained"
              className="btn btn-secondary"
            >
              Cancel
            </Button>
          </FormControl>
        </Stack>
      </DialogActions>
    </BootstrapDialog>
  );
};

export default ErrorTableDialog;
